// Regras de exibição do disparo em massa: dinheiro, o medidor de saldo do
// recibo e os números do acompanhamento. Tudo puro — a tela só desenha.

const STATUS_ATIVOS = ['pending', 'processing', 'paused_saldo']
const MOTIVO_EXPIRADO = 'Saldo insuficiente — disparo expirado'

const numero = (n) => Number(n || 0).toLocaleString('pt-BR')
const umaCasa = (n) => Math.round(n * 10) / 10

export function disparoAtivo(status) {
  return STATUS_ATIVOS.includes(status)
}

// "R$ 26,52"; negativo leva o menos tipográfico na frente ("−R$ 24,52").
export function reais(valor) {
  const centavos = Math.round((Number(valor) || 0) * 100) / 100
  const absoluto = Math.abs(centavos).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${centavos < 0 ? '−' : ''}R$ ${absoluto}`
}

// O que sai do saldo, sempre com o menos: "−R$ 26,52".
export function debito(valor) {
  return reais(-Math.abs(Number(valor) || 0))
}

// Medidor do saldo numa régua só: do ponto mais baixo (limite ou o saldo
// depois do disparo) até o saldo de agora. Da direita pra esquerda: o que já
// está reservado para envios em andamento, a fatia deste disparo (a parte que
// passa do limite fica vermelha) e o que sobra. Posições em % com uma casa.
export function geometriaDoMedidor({ atual, comprometido = 0, total, tolerancia = 0 } = {}) {
  if (!Number.isFinite(Number(atual)) || !Number.isFinite(Number(total))) {
    return { faixas: [], marcos: [], depois: null, estoura: false }
  }
  const a = Number(atual)
  const c = Math.max(0, Number(comprometido) || 0)
  const t = Math.max(0, Number(total))
  const limite = Number(tolerancia) || 0
  const livre = a - c
  const depois = Math.round((livre - t) * 100) / 100

  const baixo = Math.min(0, limite, depois, livre)
  const alto = Math.max(0, a, baixo + 1)
  const pos = (v) => umaCasa(((v - baixo) / (alto - baixo)) * 100)
  const faixas = []
  const faixa = (tipo, de, ate) => {
    if (ate <= de) return
    const esquerda = pos(de)
    faixas.push({ tipo, esquerda, largura: umaCasa(pos(ate) - esquerda) })
  }

  if (depois > 0) faixa('fica', 0, depois)
  // Saldo que já está abaixo de zero: o trecho até o zero não é "vazio", é dívida.
  if (a < 0) faixa('negativo', a, 0)
  if (c > 0) faixa('reservado', Math.max(livre, baixo), a)
  faixa('este', Math.max(depois, limite), livre)
  if (depois < limite) faixa('estoura', depois, Math.min(limite, livre))

  const marcos = [{ tipo: 'zero', em: pos(0) }]
  if (limite !== 0) marcos.push({ tipo: 'limite', em: pos(limite) })

  return { faixas, marcos, depois, estoura: depois < limite }
}

// Números do acompanhamento a partir do detalhe do master. "Saíram" é o que
// já deixou a fila (aceita ou recusada); quem ainda não virou mensagem está na
// fila enquanto o disparo anda, e conta como cancelado se ele foi cancelado.
export function numerosDoDisparo(detalhe = {}) {
  const m = detalhe.mensagens || {}
  const total = Number(detalhe.total) || 0
  const n = (k) => Number(m[k]) || 0
  const criadas = m.criadas != null
    ? n('criadas')
    : n('aceitas') + n('falhas') + n('naFila') + n('retidasSaldo') + n('retidasSmartFilter') + n('canceladas') + n('adiadas') + n('retidasHiperpay') + n('outras')
  const naoCriadas = Math.max(0, total - criadas)
  const ativo = disparoAtivo(detalhe.status)
  // Adiada pelo anti-ban (teto diário do número) ou à espera do link de
  // pagamento (HiperPay fora do ar, tenta de novo sozinha) ainda não saiu:
  // espera na fila.
  const esperando = n('naFila') + n('adiadas') + n('retidasHiperpay')
  const sairam = Math.max(0, criadas - esperando - n('retidasSaldo') - n('retidasSmartFilter') - n('canceladas'))
  const pendentes = esperando + n('retidasSaldo') + n('retidasSmartFilter')

  return {
    total,
    sairam,
    entregues: n('entregues'),
    falhas: n('falhas'),
    aCaminho: Math.max(0, sairam - n('entregues') - n('falhas')),
    naFila: esperando + (ativo ? naoCriadas : 0),
    adiadas: n('adiadas'),
    retidasSaldo: n('retidasSaldo'),
    retidasSmartFilter: n('retidasSmartFilter'),
    retidas: n('retidasSaldo') + n('retidasSmartFilter'),
    canceladas: n('canceladas') + (detalhe.status === 'canceled' ? naoCriadas : 0),
    ativo,
    // Lote concluído pode ainda ter mensagem adiada ou retida para sair — dá
    // para cancelar enquanto houver.
    podeCancelar: ativo || pendentes > 0
  }
}

// Uma frase pra linha da lista de disparos recentes.
export function resultadoDoDisparo({ status, total, cursor, canceledReason } = {}) {
  const andou = `${numero(cursor)} de ${numero(total)}`
  if (status === 'pending') return 'Começa em instantes'
  if (status === 'processing') return `Enviando · ${andou}`
  if (status === 'paused_saldo') return `Pausado por saldo · ${andou}`
  if (status === 'done') return `Concluído · ${numero(total)} destinatários`
  if (status === 'canceled') {
    return canceledReason === MOTIVO_EXPIRADO ? `Expirou sem saldo com ${andou}` : `Cancelado com ${andou}`
  }
  return 'Não foi concluído'
}

export function descreverMensagem(mensagem) {
  if (!mensagem) return '—'
  if (mensagem.tipo === 'modelo') return mensagem.nome
  return `${mensagem.previa || ''}${mensagem.arquivo ? ` + ${mensagem.arquivo}` : ''}`
}

// "23 h 10 min" até o disparo pausado expirar; vazio se já passou.
export function prazoRestante(expiraEm, agora = new Date()) {
  if (!expiraEm) return ''
  const ms = new Date(expiraEm).getTime() - agora.getTime()
  if (!(ms > 0)) return ''
  const minutos = Math.ceil(ms / 60000)
  const h = Math.floor(minutos / 60)
  const min = minutos % 60
  if (!h) return `${min} min`
  return min ? `${h} h ${min} min` : `${h} h`
}

// "16/10, 12:00" no horário de Brasília.
export function quandoFoi(data) {
  if (!data) return ''
  const d = new Date(data)
  if (Number.isNaN(d.getTime())) return ''
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).formatToParts(d)
  const p = (tipo) => partes.find((x) => x.type === tipo)?.value || ''
  return `${p('day')}/${p('month')}, ${p('hour')}:${p('minute')}`
}

export { STATUS_ATIVOS, numero }
